import { Button, Stack } from '@solvhealth/jigsaw-v1';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { shouldDisableButton } from '~/core/util/form';
import { useSolvSelector } from '~/reducers';
import { SectionPage } from './interfaces';
import { getNextButtonKey, getRequiredFieldNames, hasRequiredFields } from './util';

type Props = {
  /** The fields of the form */
  children: ReactNode;

  /** Handler for submitting the current step of paperwork */
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;

  /** Handler for skipping the current step of paperwork */
  onSkip: () => void;

  form?: object;

  sectionPage: SectionPage;
};

/**
 * Container for a paperwork form page. Adds spacing between form elements,
 * a continue/update button, and loading states.
 */
const PaperworkForm = ({ children, onSubmit, onSkip, form, sectionPage }: Props) => {
  const paperwork = useSolvSelector((state) => state.paperwork);
  const { t } = useTranslation(['common', 'paperwork']);

  const {
    fieldNames,
    showSubmit = true,
    allowSkip = false,
    allowDisableSubmit = false,
  } = sectionPage;
  const showSkip = allowSkip && !hasRequiredFields(fieldNames, paperwork);
  const requiredFieldNames = getRequiredFieldNames(fieldNames, paperwork);
  const disableSubmit = allowDisableSubmit && shouldDisableButton(form, requiredFieldNames);

  return (
    <Stack as="form" onSubmit={onSubmit} space={5}>
      <Stack space={3}>{children}</Stack>
      <Stack space={3}>
        {showSubmit && (
          <Button
            disabled={disableSubmit}
            fullWidth
            loading={paperwork.isSubmitting}
            mt={3}
            size="large"
            type="submit"
          >
            {t(getNextButtonKey(paperwork))}
          </Button>
        )}
        {showSkip && (
          <Button fullWidth onClick={onSkip} size="large" variant="text">
            {t('common:skip')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default PaperworkForm;
